/**
 * Load more for collection product grids.
 */
const LOAD_MORE_BATCH_SIZE = 8;

class CollectionInfiniteScroll {
    constructor(section) {
        this.section = section;
        this.filterContainer = section.querySelector('filter-container');
        this.sectionId = this.filterContainer?.dataset.filterSectionId;
        this.isLoading = false;
        this.abortController = null;
        this.pendingProducts = [];
        this.loadMoreClickNumber = 0;
        this.onClick = (event) => {
            event.preventDefault();
            this.loadNext();
        };
        this.init();
    }

    init() {
        if (this.sentinel) {
            this.sentinel.removeEventListener('click', this.onClick);
        }
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
        this.isLoading = false;
        this.sentinel = this.section.querySelector('[data-collection-infinite-scroll]');
        this.pendingProducts = [];
        this.totalCount = Number(this.sentinel?.dataset.totalCount || 0);
        this.visibleCount = Number(this.sentinel?.dataset.visibleCount || 0);
        if (!this.sentinel?.dataset.nextUrl) {
            return;
        }

        this.sentinel.addEventListener('click', this.onClick);
        this.updateButton();
    }

    destroy() {
        if (this.sentinel) {
            this.sentinel.removeEventListener('click', this.onClick);
        }
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    getAnalyticsSection() {
        return this.section.querySelector('[data-analytics-section]');
    }

    trackLoadMoreClick() {
        const analyticsSection = this.getAnalyticsSection();
        const visibleBefore = this.visibleCount;
        const clickNumber = this.loadMoreClickNumber + 1;
        const visibleTarget = Math.min(
            visibleBefore + LOAD_MORE_BATCH_SIZE,
            this.totalCount
        );

        this.loadMoreClickNumber = clickNumber;

        if (analyticsSection) {
            analyticsSection.dataset.afterLoadMore = 'true';
            analyticsSection.dataset.loadMoreClickNumber = String(clickNumber);
        }

        const sections = Array.from(
            document.querySelectorAll('[data-analytics-section]')
        );
        const position = sections.indexOf(analyticsSection) + 1;

        window.themeAnalytics?.send('load_more_click', {
            section_id: analyticsSection?.dataset.analyticsSectionId || '',
            section_type: analyticsSection?.dataset.analyticsSectionType || '',
            section_name: analyticsSection?.dataset.analyticsSectionName || '',
            section_position: position > 0 ? position : undefined,
            item_list_id: analyticsSection?.dataset.analyticsSectionId || '',
            item_list_name: analyticsSection?.dataset.analyticsSectionName || '',
            load_more_click_number: String(clickNumber),
            products_visible_before: String(visibleBefore),
            products_visible_target: String(visibleTarget),
            products_total: this.totalCount
        });
    }

    updateButton() {
        const remaining = Math.max(this.totalCount - this.visibleCount, 0);
        if (!this.sentinel || remaining === 0) {
            this.destroy();
            this.sentinel?.closest('.collection-infinite-scroll')?.remove();
            this.sentinel = null;
            return;
        }

        const label = this.sentinel.querySelector('.collection-infinite-scroll__next-label');
        const labelTemplate = this.sentinel.dataset.labelTemplate || 'Load More (__COUNT__)';
        if (label) {
            const labelText = labelTemplate.replace('__COUNT__', remaining);
            if (label.firstChild) {
                label.firstChild.nodeValue = labelText;
            } else {
                label.textContent = labelText;
            }
        }
    }

    appendNextBatch() {
        const currentGrid = this.section.querySelector('.filters-adjacent .product-grid');
        if (!currentGrid) {
            return;
        }

        const nextProducts = this.pendingProducts.splice(0, LOAD_MORE_BATCH_SIZE);
        nextProducts.forEach((product) => currentGrid.appendChild(product));
        this.visibleCount += nextProducts.length;

        const analyticsSection = this.getAnalyticsSection();
        if (analyticsSection) {
            analyticsSection.dataset.productsVisible = String(this.visibleCount);
        }

        this.updateButton();
    }

    loadNext() {
        if (this.isLoading || !this.sentinel) {
            return;
        }

        const hasPendingProducts = this.pendingProducts.length > 0;
        const nextUrl = this.sentinel.dataset.nextUrl;

        if (!hasPendingProducts && !nextUrl) {
            return;
        }

        this.trackLoadMoreClick();

        if (hasPendingProducts) {
            this.appendNextBatch();
            return;
        }

        this.isLoading = true;
        this.sentinel.classList.add('collection-infinite-scroll--loading');
        this.sentinel.setAttribute('aria-busy', 'true');

        let fetchUrl = nextUrl;
        if (this.sectionId) {
            fetchUrl += `${fetchUrl.indexOf('?') >= 0 ? '&' : '?'}section_id=${this.sectionId}`;
        }

        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();

        fetch(fetchUrl, {
            method: 'get',
            signal: this.abortController.signal
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((html) => {
                const template = document.createElement('template');
                template.innerHTML = html;

                const newGrid = template.content.querySelector('.filters-adjacent .product-grid');
                if (!newGrid) {
                    throw new Error('Collection product grid not found');
                }
                this.pendingProducts = [...newGrid.children];

                const newSentinel = template.content.querySelector('[data-collection-infinite-scroll]');
                if (newSentinel?.dataset.nextUrl) {
                    this.sentinel.dataset.nextUrl = newSentinel.dataset.nextUrl;
                    this.sentinel.setAttribute('href', newSentinel.dataset.nextUrl);
                } else this.sentinel.dataset.nextUrl = '';
                this.appendNextBatch();
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.warn(error);
                }
            })
            .finally(() => {
                this.isLoading = false;
                if (this.sentinel) {
                    this.sentinel.classList.remove('collection-infinite-scroll--loading');
                    this.sentinel.removeAttribute('aria-busy');
                }
            });
    }
}

function collectionInfiniteScrollSelfCheck() {
    const cases = [
        [24, 100, 1, 32],
        [32, 100, 2, 40],
        [96, 100, 3, 100]
    ];
    const valid = cases.every(([visible, total, clickNumber, target]) => (
        Math.min(visible + LOAD_MORE_BATCH_SIZE, total) === target
        && clickNumber > 0
    ));

    console.assert(valid, 'collection Load More tracking calculation');
    return valid;
}

function getCollectionSection(el) {
    return el.closest('.shopify-section');
}

function initCollectionInfiniteScroll() {
    document.querySelectorAll('.shopify-section').forEach((section) => {
        if (!section.querySelector('filter-container[data-filter-section-id]')
            && !section.querySelector('[data-collection-infinite-scroll]')) {
            return;
        }

        if (section._collectionInfiniteScroll) {
            section._collectionInfiniteScroll.destroy();
        }
        section._collectionInfiniteScroll = new CollectionInfiniteScroll(section);
    });
}

function watchCollectionAjaxUpdates() {
    document.querySelectorAll('.collection-infinite-scroll[data-ajax-container]').forEach((container) => {
        if (container._infiniteScrollWatcher) {
            return;
        }
        container._infiniteScrollWatcher = new MutationObserver(() => {
            const section = getCollectionSection(container);
            if (!section?._collectionInfiniteScroll) {
                return;
            }
            clearTimeout(section._infiniteScrollReinitTimer);
            section._infiniteScrollReinitTimer = setTimeout(() => {
                section._collectionInfiniteScroll.init();
            }, 50);
        });
        container._infiniteScrollWatcher.observe(container, { childList: true });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initCollectionInfiniteScroll();
        watchCollectionAjaxUpdates();
    });
} else {
    initCollectionInfiniteScroll();
    watchCollectionAjaxUpdates();
}

if (new URLSearchParams(window.location.search).get('ga_debug') === '1') {
    collectionInfiniteScrollSelfCheck();
}
